// What runs once when the app loads in server mode (X3-01): the service worker (offline shell, background sync, push), the
// offline write queue, the language the signed-in user saved (X11-411a), and the workspace's time zone and clock. Everything
// here is quiet when it cannot work: no session yet, no service worker support, a dev server. Public pages (proposal, pay,
// sign) and the sign-in pages never read the settings: a visitor has no session there, and the browser would log the 401.
// The client portal reads the signed-in contact's own language, and never the staff settings.
import type { QueryClient } from "@tanstack/react-query";
import { LOCALE } from "@offsilo/contracts/settings";
import { setFormatPrefs } from "@offsilo/ui/format";
import { hasSession, serverAuth } from "@/data/auth";
import { call } from "@/data/server-client";
import { syncFavourites } from "./favourites";
import { applyUserLocale } from "./locale";
import { bootNativePush } from "./pwa/native-push";
import { registerServiceWorker } from "./pwa/push";
import { flushQueue } from "./pwa/queue";

/**
 * The language and formats applied from now on, once the session is known: a portal contact's own saved language on the
 * portal, and on staff pages the workspace's language and formats (the people contract has no language of its own). False
 * when there is no session or nothing is saved.
 */
export async function applySavedLocale(queryClient: QueryClient): Promise<boolean> {
  if (typeof window !== "undefined" && portalPage(window.location.pathname)) return (await signedIn()) && applyPortalContactLocale();
  if (typeof window !== "undefined" && !staffPage(window.location.pathname)) return false;
  if (!(await signedIn())) return false;
  try {
    // through `call`: a dev server signs in first; a built app gets here only with a session (signedIn above)
    const all = await call<Record<string, unknown>>("/settings");
    queryClient.setQueryData(["settings"], all);
    const saved = LOCALE.safeParse(all.locale);
    if (saved.success) {
      setFormatPrefs({ locale: saved.data.language, timeZone: saved.data.timeZone, hour12: saved.data.hour12 });
      applyUserLocale(saved.data.language);
    }
    // the person's own language and theme come after the workspace's, so they win (X15-0103e)
    const own = await applyOwnPrefs();
    return saved.success || own;
  } catch {
    return false;
  }
}

/**
 * Whether boot may read the staff settings. Signed out in a built app it must not: `call` answers a 401 by loading
 * /sign-in?next= as a new page, so a deep link fetched every script twice (2026-10-31, 474 script files on
 * /settings/account, the portal's /workspace read did the same on /portal/*). The route guards send the person to sign-in
 * without a reload. A dev server signs
 * in on the first call, so it always may.
 */
async function signedIn(): Promise<boolean> {
  return import.meta.env.DEV || (await hasSession()) !== false;
}

/** X15-0103e: the signed-in person's own saved language and theme. A failed read keeps what is shown now. */
async function applyOwnPrefs(): Promise<boolean> {
  try {
    const prefs = await call<Record<string, unknown>>("/me/prefs");
    let applied = false;
    if (typeof prefs.language === "string" && applyUserLocale(prefs.language)) applied = true;
    if (prefs.theme === "light" || prefs.theme === "dark" || prefs.theme === "system") {
      // the same write as setTheme in commands.tsx, kept here so boot does not load the command palette module
      try {
        localStorage.setItem("os-theme", prefs.theme);
      } catch {}
      const dark = prefs.theme === "dark" || (prefs.theme === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
      document.documentElement.dataset.theme = dark ? "dark" : "light";
      applied = true;
    }
    return applied;
  } catch {
    return false;
  }
}

/** Pages a visitor reaches without a staff session: public links, the client portal, sign-in and sign-up, invites, the trust page. */
const PUBLIC_PREFIXES = ["/p/", "/portal", "/freelancer", "/help/", "/invite", "/join", "/n/", "/setup", "/sign-in", "/sign-up", "/trust"];
export const staffPage = (path: string): boolean => !PUBLIC_PREFIXES.some((p) => path === p.replace(/\/$/, "") || path.startsWith(p));

/** The client portal, where a signed-in contact reads its own language. The sign-in page is reached before any session exists. */
const portalPage = (path: string): boolean => path.startsWith("/portal") && !path.startsWith("/portal/sign-in");

/**
 * The signed-in contact's saved language. `me` in /workspace is the contact the session belongs to, and its `locale` field is
 * the language the portal's own screens follow (X11-411a). False when the contact has none, or the app has no messages for it.
 */
async function applyPortalContactLocale(): Promise<boolean> {
  try {
    const { me } = await call<{ me?: string }>("/workspace");
    if (!me) return false;
    const contact = await call<{ locale?: string }>(`/contacts/${encodeURIComponent(me)}`);
    return applyUserLocale(contact.locale);
  } catch {
    return false;
  }
}

/** Sends the writes kept offline now, and again each time the browser comes back online; refreshes the lists when any went. */
function keepQueueFlushed(queryClient: QueryClient): void {
  const flush = () =>
    flushQueue()
      .then((sent) => {
        if (sent > 0) void queryClient.invalidateQueries();
      })
      .catch(() => undefined);
  void flush();
  window.addEventListener("online", () => void flush());
}

/** Call once at start-up. `onLocale` runs when the saved language or formats were applied, so the open screen can redraw with them. */
export function bootRuntime(queryClient: QueryClient, onLocale?: () => void): void {
  if (!serverAuth || typeof window === "undefined") return;
  // the service worker caches built assets by name; a dev server's modules must never be cached
  if (!import.meta.env.DEV) void registerServiceWorker();
  keepQueueFlushed(queryClient);
  // the native shell only (X13-1009c): in a browser this resolves at once and the plugin is never loaded
  void bootNativePush();
  void applySavedLocale(queryClient).then((applied) => {
    if (applied) onLocale?.();
  });
  if (staffPage(window.location.pathname)) void signedIn().then((yes) => (yes ? syncFavourites() : undefined));
}
